import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Grid from '@mui/material/Unstable_Grid2';
import { Box, Stack, useTheme, Typography } from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';
import { useFileHandler } from 'src/hooks/use-file-handler';

import { mediaIdOf } from 'src/utils/media';

import Icon from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { MediaImage, MediaDocumentViewer } from 'src/components/media';

ResultsDetails.propTypes = {
  admission: PropTypes.object,
};

export default function ResultsDetails({ admission }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { downloadMedia, downloadMediasAsZip } = useFileHandler();

  const mdUp = useResponsive('up', 'md');

  // Los resultados ya no traen el binario en base64: se piden al endpoint de streaming
  // del backend. El portal entra con la cuenta del propio paciente, asi que el backend
  // solo le sirve sus documentos.
  const handleDownload = async (media, fileName) => {
    const result = await downloadMedia(media, { fileName });

    enqueueSnackbar(result.message, { variant: result.success ? 'success' : 'error' });
  };

  return (
    <Grid container spacing={3} sx={{ width: '100%' }}>
      {admission.medicalServices.map((service, index) => (
        <Grid xs={12} md={12} sm={12} key={index}>
          <Box
            sx={{
              borderRadius: 2,
              backgroundColor: alpha(
                theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[700],
                0.4
              ),

              p: 1.5,
              pl: 2,
            }}
          >
            <Stack direction="row" alignItems="center" pl={2}>
              <Typography variant="h6">{service.serviceName}</Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              mt: 2,
              borderRadius: 2,
              backgroundColor: alpha(
                theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[700],
                0.4
              ),

              p: 1.5,
              pl: 2,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent={{ xs: 'center', sm: 'space-between' }}
              alignItems={{ xs: 'center', sm: 'flex-start' }}
              mb={2}
            >
              <Typography variant="h6" pl={2}>
                Resumen General
              </Typography>
              <Button
                variant={mdUp ? 'text' : 'contained'}
                color="inherit"
                disabled={!mediaIdOf(service.admitionServiceMedia)}
                onClick={() =>
                  handleDownload(
                    service.admitionServiceMedia,
                    `${service.serviceName.toLowerCase()}_resumen`
                  )
                }
                sx={{
                  alignSelf: { xs: 'center', sm: 'flex-start' },
                  pl: { xs: 1, sm: 1 },
                }}
              >
                <Icon icon="mdi:download" width={20} height={20} />
                <Typography variant="caption" pl={1} sx={{ fontWeight: 'bold' }}>
                  Descargar Resumen
                </Typography>
              </Button>
            </Stack>
            {service.admitionServiceMedia.fileType.includes('image') && (
              <Stack
                direction="column"
                spacing={3}
                justifyContent="center"
                alignItems="center"
                width="100%"
              >
                <MediaImage
                  media={service.admitionServiceMedia}
                  alt="GeneralSummary"
                  sx={{
                    borderRadius: 2,
                    width: '100%',
                    height: 'auto',
                    maxWidth: { xs: '100%', sm: '90%', md: '85%' },
                    minHeight: { xs: 'auto', sm: 400, md: 500 },
                    objectFit: 'contain',
                  }}
                />
              </Stack>
            )}
            {service.admitionServiceMedia.fileType.includes('application/pdf') && (
              <MediaDocumentViewer
                media={service.admitionServiceMedia}
                title={`Resumen del ${service.serviceName.toLowerCase()}`}
                height="90vh"
                sx={{ borderRadius: '20px', px: 0.5 }}
              />
            )}
            {service.medicalImages.length > 0 && (
              <>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  pb={3}
                  pt={3}
                  spacing={2}
                  justifyContent={{ xs: 'center', sm: 'space-between' }}
                  alignItems={{ xs: 'center', sm: 'flex-start' }}
                >
                  <Typography variant="h4" pl={2}>
                    Imágenes Médicas del {service.serviceName.toLowerCase()}
                  </Typography>
                  <Button
                    variant={mdUp ? 'text' : 'contained'}
                    color="inherit"
                    disabled={!service.medicalImages.length}
                    onClick={async () => {
                      const result = await downloadMediasAsZip(
                        service.medicalImages,
                        `${service.serviceName.toLowerCase()}_imagenes`
                      );
                      enqueueSnackbar(result.message, {
                        variant: result.success ? 'success' : 'error',
                      });
                    }}
                    sx={{
                      alignSelf: { xs: 'center', sm: 'flex-start' },
                      pl: { xs: 1, sm: 1 },
                    }}
                  >
                    <Icon icon="mdi:download" width={20} height={20} />
                    <Typography variant="subtitle" pl={1}>
                      Descargar imágenes
                    </Typography>
                  </Button>
                </Stack>
                <Stack
                  direction="column"
                  spacing={3}
                  justifyContent="center"
                  width="100%"
                  alignItems="center"
                >
                  {service.medicalImages.map((image) => (
                    <MediaImage
                      key={image.mediaId}
                      media={image}
                      alt="MedicalImages"
                      sx={{
                        borderRadius: 2,
                        width: '100%',
                        height: 'auto',
                        maxWidth: { xs: '100%', sm: '90%', md: '85%' },
                        minHeight: { xs: 'auto', sm: 'auto', md: 'auto' },
                        objectFit: 'contain',
                      }}
                    />
                  ))}
                </Stack>
              </>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}
